import { create } from 'zustand'
import type { SelectedTankArray } from '@/types/view'
import type { ValidationError } from '@/utils/assignGroupValidation'

type HomeAssignGroupState = {
  // Step 1: Selected ungrouped tanks
  selectedTanks: SelectedTankArray
  selectTanksBorderColor: string

  // Step 2: Selected group
  selectedGroupId: string | null
  selectedGroupName: string | null
  selectGroupBorderColor: string

  // Computed for API
  schoolIds: string[]

  // Validation state
  validationErrors: ValidationError[]
}

type HomeAssignGroupAction = {
  setSelectedTanks: (tanks: SelectedTankArray) => void
  setSelectedGroup: (groupId: string, groupName: string) => void
  setBorderColor: () => void
  setGroupBorderColor: () => void
  setValidationErrors: (errors: ValidationError[]) => void
  clearValidationErrors: () => void
  reset: () => void
}

const initialState: HomeAssignGroupState = {
  selectedTanks: [],
  selectTanksBorderColor: 'border border-tdpldarkgrey',
  selectedGroupId: null,
  selectedGroupName: null,
  selectGroupBorderColor: 'border border-tdpldarkgrey',
  schoolIds: [],
  validationErrors: [],
}

const useHomeAssignGroupStore = create<HomeAssignGroupState & HomeAssignGroupAction>((set) => ({
  ...initialState,

  setSelectedTanks: (selectedTanks) => {
    set(() => ({
      selectedTanks,
      selectTanksBorderColor: 'border border-tdpldarkgrey',
      // Extract schoolIds from selected tanks (tankId maps to schoolId via TankRelationMap)
      schoolIds: selectedTanks
        .map((tank) => tank.tankId)
        .filter((id): id is string => id !== null),
    }))
  },

  setSelectedGroup: (groupId, groupName) => {
    set(() => ({
      selectedGroupId: groupId,
      selectedGroupName: groupName,
      selectGroupBorderColor: 'border border-tdpldarkgrey',
    }))
  },

  setBorderColor: () => {
    set((state) => ({
      selectTanksBorderColor:
        state.selectedTanks.length !== 0
          ? 'border border-tdpldarkgrey'
          : 'border-2 border-tdplmaroon',
    }))
  },

  setGroupBorderColor: () => {
    set((state) => ({
      selectGroupBorderColor:
        state.selectedGroupId !== null
          ? 'border border-tdpldarkgrey'
          : 'border-2 border-tdplmaroon',
    }))
  },

  setValidationErrors: (errors) => {
    set(() => ({ validationErrors: errors }))
  },

  clearValidationErrors: () => {
    set(() => ({ validationErrors: [] }))
  },

  reset: () => {
    set(initialState)
  },
}))

export default useHomeAssignGroupStore
